import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../config';

// 판정 경로는 200(판정)·404(미등록 주소)·500 만 준다. 그 밖의 실패(망 오류·5xx·서버 앞단이 막은 4xx)는 판정이 아니다 —
// 권한 있음으로도 없음으로도 두지 않고 '확인하는 중' 화면에서 다시 묻는다.
// WHY: 권한 없음으로 두면 유저 API 재시작 몇 초 사이나 망 오류 때 들어온 사람까지 빈 화면이 됐고, 앞단의 차단이 토큰 거절로 읽혀 로그아웃됐다.
//      권한 있음으로 두면 배정 0건 주소가 그 사이 로그인·회원가입 화면을 노출한다(App.js [2026-07-28 원복] 사양 위반).
const isNotVerdict = (error) => error.response?.status !== 404;
const RETRY_DELAYS_MS = [1500, 3000, 5000, 10000]; // 마지막 값으로 계속 묻는다 — 서버가 돌아오면 바로 화면이 열린다

/**
 * 도메인 접근 권한 체크 훅
 */
const useDomainPermission = () => {
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [permissionDetails, setPermissionDetails] = useState(null);
  const retryRef = useRef({ attempt: 0, timer: null });

  useEffect(() => {
    checkDomainPermission();
    return () => clearTimeout(retryRef.current.timer);
  }, []);

  const retryLater = () => {
    const state = retryRef.current;
    const delay = RETRY_DELAYS_MS[Math.min(state.attempt, RETRY_DELAYS_MS.length - 1)];
    state.attempt += 1;
    state.timer = setTimeout(checkDomainPermission, delay);
  };

  // 방문자 판정 — 주소가 등록돼 있고 배정이 있어야 화면을 연다(배정 0건 주소는 로그인 화면도 열지 않는다: App.js [2026-07-28 원복]).
  const checkGuestAccess = async (API_BASE_URL, domainName) => {
    console.log('비로그인 상태: 도메인 존재 여부 체크');

    try {
      // 도메인이 존재하고 할당되어 있는지 확인
      const response = await axios.get(
        `${API_BASE_URL}/domain-access/check-domain-exists/${encodeURIComponent(domainName)}`
      );

      if (response.data.success && response.data.domainExists && response.data.hasAssignments) {
        console.log('도메인이 존재하고 할당됨 - 접근 허용');
        setHasPermission(true);
        setPermissionDetails({
          accessType: 'guest',
          message: '로그인이 필요합니다.'
        });
      } else if (response.data.success && response.data.domainExists && !response.data.hasAssignments) {
        console.log('도메인은 존재하지만 할당 없음 - 접근 차단');
        setHasPermission(false);
        setPermissionError('도메인에 할당된 사용자가 없습니다.');
      } else {
        console.log('도메인이 존재하지 않음 - 접근 차단');
        setHasPermission(false);
        setPermissionError('등록되지 않은 도메인입니다.');
      }
    } catch (error) {
      console.error('도메인 존재 확인 실패:', error);
      if (isNotVerdict(error)) {
        retryLater();
        return;
      }
      setPermissionError('등록되지 않은 도메인입니다.');
      setHasPermission(false);
    }

    setPermissionLoading(false);
  };

  // 이 토큰이 이 주소에서 거절됐다 — 토큰을 지우고 페이지를 새로 열어 방문자로 판정받는다(로그인 주소로 가므로 토큰 없이 한 번만 돈다).
  // WHY: 권한 없음으로 끝내면 로그인 화면이 비고 그 화면에서 로그인해도 판정이 그대로라 다시 튕겼다.
  //      같은 페이지에서 방문자 판정만 다시 하면 이미 읽어 둔 회원 정보가 남아 토큰 없이 로그인된 화면이 그려진다.
  //      새로 열면 배정 있는 주소는 로그인 화면이, 배정 0건 주소는 막힌 그대로가 된다. 그동안 '확인하는 중' 화면을 유지한다.
  const restartAsGuest = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/login');
  };

  const checkDomainPermission = async () => {
    // 현재 도메인 URL
    const currentDomain = window.location.origin;
    const domainName = window.location.hostname; // 도메인 이름만 추출
    // API URL 설정 - config에서 가져오기
    const API_BASE_URL = config.API_URL;

    try {
      setPermissionLoading(true);

      console.log('[DomainPermission] Checking domain:', currentDomain, 'hostname:', domainName);

      // 로컬 개발 환경 체크 (localhost는 권한 체크 스킵)
      if (currentDomain.includes('localhost') || currentDomain.includes('127.0.0.1')) {
        console.log('개발 환경: 도메인 권한 체크 스킵');
        setHasPermission(true);
        setPermissionDetails({
          accessType: 'development',
          message: '개발 환경에서는 권한 체크를 스킵합니다.'
        });
        setPermissionLoading(false);
        return;
      }

      // 토큰 확인
      const token = localStorage.getItem('token');

      // JWT 토큰 만료 체크
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiry = payload.exp * 1000; // exp는 초 단위
          if (Date.now() > expiry) {
            console.log('토큰 만료됨 - 삭제');
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            // 토큰 만료되었으므로 도메인 존재 여부만 체크
          }
        } catch (e) {
          console.error('토큰 파싱 실패:', e);
          localStorage.removeItem('token');
        }
      }

      // 유효한 토큰이 없는 경우 - 도메인 존재 여부만 체크
      const validToken = localStorage.getItem('token'); // 위에서 삭제되었을 수 있음
      if (!validToken) {
        await checkGuestAccess(API_BASE_URL, domainName);
        return;
      }

      // 로그인한 사용자 - 도메인 권한 확인
      console.log('로그인 상태: 도메인 권한 체크', {
        currentDomain,
        API_BASE_URL,
        token: 'exists'
      });

      const response = await axios.get(
        `${API_BASE_URL}/domain-access/check/${encodeURIComponent(domainName)}`,
        {
          headers: {
            'Authorization': `Bearer ${validToken}`
          }
        }
      );

      if (response.data.success && response.data.hasAccess) {
        console.log('도메인 접근 권한 확인:', response.data);
        setHasPermission(true);
        setPermissionDetails({
          accessType: response.data.accessType,
          inheritedFrom: response.data.inheritedFrom,
          message: response.data.accessType === 'direct'
            ? '직접 할당된 도메인입니다.'
            : `${response.data.inheritedFrom}님으로부터 상속된 권한입니다.`
        });
        setPermissionLoading(false);
      } else {
        console.log('도메인 접근 권한 없음:', response.data);
        restartAsGuest();
      }
    } catch (error) {
      console.error('도메인 권한 확인 실패:', error);
      if (isNotVerdict(error)) {
        retryLater();
        return;
      }

      // 404: 도메인이 등록되지 않음
      setPermissionError('등록되지 않은 도메인입니다.');
      setHasPermission(false);
      setPermissionLoading(false);
    }
  };

  return {
    hasPermission,
    permissionLoading,
    permissionError,
    permissionDetails,
    checkDomainPermission
  };
};

export default useDomainPermission;
